import { initializeApp } from 'firebase/app'
import { 
  getAuth, 
  GoogleAuthProvider,
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth'

// Firebase configuration from environment (same format as main app)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const app = initializeApp(firebaseConfig)
export { app }
const auth = getAuth(app)

// Auth providers
const googleProvider = new GoogleAuthProvider()

// Configure providers
googleProvider.addScope('email')
googleProvider.addScope('profile')

export interface AdminUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
}

class AuthService {
  private currentUser: AdminUser | null = null
  private authStateListeners: ((user: AdminUser | null) => void)[] = []

  constructor() {
    // Listen for auth state changes
    onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        // Get ID token for API calls
        const token = await firebaseUser.getIdToken(true)
        localStorage.setItem('firebase_token', token)
        
        // Set current user
        this.currentUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
        }
        localStorage.setItem('admin_user', JSON.stringify(this.currentUser))
      } else {
        // Clear user data
        this.currentUser = null
        localStorage.removeItem('firebase_token')
        localStorage.removeItem('admin_user')
      }
      
      // Notify listeners
      this.authStateListeners.forEach(listener => listener(this.currentUser))
    })
  }

  async signInWithGoogle(): Promise<AdminUser> {
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user
      
      // Get fresh ID token
      const token = await user.getIdToken(true)
      localStorage.setItem('firebase_token', token)
      
      const adminUser: AdminUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
      }
      
      this.currentUser = adminUser
      localStorage.setItem('admin_user', JSON.stringify(adminUser))
      
      return adminUser
    } catch (error: any) {
      throw new Error(`Google sign-in failed: ${error.message}`)
    }
  }


  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth)
      this.currentUser = null
      localStorage.removeItem('firebase_token')
      localStorage.removeItem('admin_user')
    } catch (error: any) {
      throw new Error(`Sign-out failed: ${error.message}`)
    }
  }

  getCurrentUser(): AdminUser | null {
    return this.currentUser
  }

  getCurrentToken(): string | null {
    return localStorage.getItem('firebase_token')
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null
  }

  // Add auth state listener
  onAuthStateChange(callback: (user: AdminUser | null) => void): () => void {
    this.authStateListeners.push(callback)
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback)
      if (index > -1) {
        this.authStateListeners.splice(index, 1)
      }
    }
  }

  // Check if user is admin (this would typically be done on the backend)
  async checkAdminStatus(): Promise<boolean> {
    try {
      const token = this.getCurrentToken()
      if (!token) {
        console.log('No token found for admin check')
        return false
      }

      console.log('Checking admin status with token:', token.substring(0, 20) + '...')

      // Make a request to a simple admin status endpoint (doesn't depend on complex data)
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/admin/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      console.log('Admin status check response:', response.status)
      
      if (response.status === 403) {
        console.log('User is not an admin (403 Forbidden)')
        return false
      }
      
      if (response.status === 200) {
        console.log('User is an admin (200 OK)')
        return true
      }
      
      if (response.status === 500) {
        console.log('Server error during admin status check - assuming admin access but server has issues')
        // For 500 errors, we assume the user is admin but the server has issues
        // This prevents the "Access Denied" screen when there are server problems
        return true
      }
      
      console.log('Unexpected response status:', response.status)
      return false
    } catch (error) {
      console.error('Error checking admin status:', error)
      return false
    }
  }

  // Refresh token
  async refreshToken(): Promise<string | null> {
    const user = auth.currentUser
    if (user) {
      try {
        const token = await user.getIdToken(true)
        localStorage.setItem('firebase_token', token)
        return token
      } catch (error) {
        console.error('Failed to refresh token:', error)
        return null
      }
    }
    return null
  }

  // Initialize from stored data (for page refresh)
  initializeFromStorage(): void {
    const storedUser = localStorage.getItem('admin_user')
    const storedToken = localStorage.getItem('firebase_token')
    
    if (storedUser && storedToken) {
      try {
        this.currentUser = JSON.parse(storedUser)
      } catch (error) {
        console.error('Failed to parse stored user data:', error)
        localStorage.removeItem('admin_user')
        localStorage.removeItem('firebase_token')
      }
    }
  }
}

// Export singleton instance
export const authService = new AuthService()

// Initialize from storage on load
authService.initializeFromStorage()

export default authService
